import { useEffect, useState } from 'react';
import { addUserIdToGTMDataLayer, getErrorMessage } from 'src/utils';
import { posthog } from 'posthog-js';
import useSafeLogger from 'src/hooks/useSafeLogger';
import {
  useAppSwitcherTooltipActions,
  useGoogleResourcesAccess,
  useSession,
  useUserLocation,
  useUserSettingsUpdate,
} from 'src/hooks';
import log from 'src/utils/logger';
import { LocationSource, SettingKeys, UserTier } from 'src/types';
import {
  useLazyGetSettingsQuery,
  useLazyGetUserByIdQuery,
} from 'src/store/services';
import { usePrefetch } from 'src/store/services/tokenApi';
import { useChoosePlanModal } from 'src/hooks/useChoosePlanModal';
import { useNotificationModal } from '../useNotificationModal';
import { useLazyGetLastSeenNotificationIdQuery } from 'src/store/services';
import { LATEST_NOTIFICATION_ID } from 'src/latest_notification';
import { useFeatureFlagEnabled } from 'posthog-js/react';

export const useAuthUser = () => {
  const { appUser } = useSession();
  const { pgLogInfo, pgLogError } = useSafeLogger();
  const [isUserLoading, setIsUserLoading] = useState(false);
  const [triggerGetUserById] = useLazyGetUserByIdQuery();
  const [triggerGetSettings] = useLazyGetSettingsQuery();
  const { updateUserSetting } = useUserSettingsUpdate();
  const { toggleShowAppSwitcherTooltip } = useAppSwitcherTooltipActions();
  const disableRequestsInMNForFreeTier = useFeatureFlagEnabled(
    'disableRequestsInMNForFreeTier',
  );

  const [triggerGetLastSeenNotificationId] =
    useLazyGetLastSeenNotificationIdQuery();

  const prefetchGoogleToken = usePrefetch('getGoogleToken');

  const { setLocationInterval } = useUserLocation();

  const { canAccessGoogleDrive, currentProvider } = useGoogleResourcesAccess();

  const { isTimeToShowPlanModal, changeChoosePlanModalState } =
    useChoosePlanModal();

  const { changeShouldShowNotificationModal } = useNotificationModal();

  useEffect(() => {
    if (appUser?.user_id && canAccessGoogleDrive && currentProvider?.email) {
      prefetchGoogleToken({
        userId: appUser.user_id,
        email: currentProvider.email,
      });
    }
  }, [
    appUser?.user_id,
    canAccessGoogleDrive,
    currentProvider?.email,
    prefetchGoogleToken,
  ]);

  const fetchUserInfo = async (userId: string) => {
    try {
      if (!appUser?.user_id) {
        setIsUserLoading(true);
      }

      const { data: userData } = await triggerGetUserById(userId, true);
      let tier_id = 'unknown';
      // set type to the resultAction
      if (userData?.location?.source === LocationSource.GPS) {
        setLocationInterval();
      }

      if (userData?.tier_id) {
        tier_id = userData.tier_id;

        if (tier_id === UserTier.OPEN) {
          toggleShowAppSwitcherTooltip(true);
        }

        if (tier_id === UserTier.FREE) {
          if (!disableRequestsInMNForFreeTier) {
            if (isTimeToShowPlanModal) {
              // keep it for now - remove if needed in the future
              // changeShouldShowPlanModal(true);
            } else {
              changeChoosePlanModalState({
                shouldShow: false,
                lastTimeWasShown: null,
              });
            }
          }
        }
      }

      pgLogInfo('User logged in');

      if (userData) {
        const { user_id, tier_id, idp, email, first_name, last_name } =
          userData;

        // Google Tag Manager add user_id, tier_id and login_type to dataLayer
        addUserIdToGTMDataLayer({
          user_id,
          tier_id,
          login_type: idp,
          user_email: email,
          user_first_name: first_name || '',
          user_last_name: last_name || '',
        });

        posthog.identify(user_id, {
          email: email,
          name: [first_name, last_name].join(' '),
          tier_id,
        });
      }

      // tome-zone initial setup
      const manuallyResult = await triggerGetSettings({
        user_id: userId,
        settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
      });
      const automaticallyResult = await triggerGetSettings({
        user_id: userId,
        settingKey: SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY,
      });
      const currentTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const isTimezoneSetAutomatically =
        !!automaticallyResult?.data?.settings?.[
          SettingKeys.IS_TIMEZONE_SET_AUTOMATICALLY
        ]?.value;
      const manuallySettingTimezone =
        manuallyResult?.data?.settings?.[SettingKeys.MANUALLY_SELECTED_TIMEZONE]
          ?.value;

      if (
        isTimezoneSetAutomatically &&
        manuallySettingTimezone !== currentTimezone
      ) {
        await updateUserSetting({
          value: currentTimezone,
          hideToast: true,
          settingKey: SettingKeys.MANUALLY_SELECTED_TIMEZONE,
        });
      }

      // announcements initial load
      const { data } = await triggerGetLastSeenNotificationId({
        user_id: userId,
      });

      if (
        data &&
        data.last_seen_id !== null &&
        data.last_seen_id !== undefined &&
        data.last_seen_id < LATEST_NOTIFICATION_ID
      ) {
        changeShouldShowNotificationModal(true);
      }

      setIsUserLoading(false);
    } catch (e: unknown) {
      pgLogError(getErrorMessage(e), {
        user_id: appUser?.user_id,
      });
      log.error(e);
      setIsUserLoading(false);
    }
  };

  return {
    fetchUserInfo,
    isUserLoading,
  };
};
